import React, { useState, useEffect } from 'react'
import { Form, Input, Upload, message, Modal } from 'antd'
import Button from '../../../components/common/button/button'
import { InboxOutlined } from '@ant-design/icons'
import { createVendor, updateVendor } from '../../../api/vendorApiService/vendorApiUtils'
import { useMsal } from '@azure/msal-react'

const { TextArea } = Input
const { Dragger } = Upload

const VendorForm = ({ visible, onCancel, onSubmit, editingVendor }) => {
  const [form] = Form.useForm()
  const [birFileList, setBirFileList] = useState([])
  const [generalFileList, setGeneralFileList] = useState([])
  const [loading, setLoading] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const { instance: msalInstance } = useMsal()

  // Initialize form with editing vendor data
  useEffect(() => {
    if (visible) {
      if (editingVendor) {
        // Populate form with existing vendor data
        form.setFieldsValue({
          vendorName: editingVendor.vendorName,
          tinNo: editingVendor.tinNumber,
          sapReferenceId: editingVendor.sapReferenceId,
          vendorAddress: editingVendor.vendorAddress
        })

        // Note: In edit mode, attachments are not shown or editable
        // They can only be managed through the attachment icons in the main table
        setBirFileList([])
        setGeneralFileList([])
      } else {
        // Reset form for new vendor
        form.resetFields()
        setBirFileList([])
        setGeneralFileList([])
      }
      setIsFormValid(false)
    }
  }, [visible, editingVendor, form])

  // Check form validity whenever form values or file lists change
  useEffect(() => {
    checkFormValidity()
  }, [birFileList])

  const checkFormValidity = () => {
    if (editingVendor) {
      // For edit mode, form is valid if at least one field has content
      // Attachments are not editable in edit mode
      const fieldValues = form.getFieldsValue()
      const hasAnyField = fieldValues.vendorName ||
        fieldValues.vendorAddress ||
        fieldValues.tinNo ||
        fieldValues.sapReferenceId
      setIsFormValid(hasAnyField)
    } else {
      // For new vendor, require name, TIN, address, and BIR attachment
      const fieldValues = form.getFieldsValue()
      const hasRequiredFields = fieldValues.vendorName &&
        fieldValues.tinNo &&
        fieldValues.vendorAddress &&
        birFileList.length > 0
      setIsFormValid(hasRequiredFields)
    }
  }

  const handleFormChange = () => {
    // Small delay to ensure form values are updated
    setTimeout(() => {
      checkFormValidity()
    }, 100)
  }

  const handleSubmit = async (values) => {
    try {
      setLoading(true)

      if (editingVendor) {
        // Update existing vendor - only include form fields, no attachments
        // Only include fields that have values (TIN is now optional)
        const updateData = {}

        if (values.vendorName) {
          updateData.vendorName = values.vendorName
        }
        if (values.tinNo) {
          updateData.tinNumber = values.tinNo
        }
        if (values.sapReferenceId) {
          updateData.sapReferenceId = values.sapReferenceId
        }
        if (values.vendorAddress) {
          updateData.vendorAddress = values.vendorAddress
        }

        console.log('Sending update data:', updateData)
        console.log('Vendor ID:', editingVendor.vendorID || editingVendor.id)

        const response = await updateVendor(editingVendor.vendorID || editingVendor.id, updateData, msalInstance)
        message.success(response.message || 'Vendor updated successfully!')
      } else {
        // Create new vendor
        const formData = {
          ...values,
          birAttachment: birFileList,
          generalAttachments: generalFileList
        }

        const response = await createVendor(formData, msalInstance)
        message.success(response.message || 'Vendor created successfully!')
      }

      // Call the parent's onSubmit function to refresh the list
      await onSubmit()

      // Reset form and close modal
      form.resetFields()
      setBirFileList([])
      setGeneralFileList([])
      setIsFormValid(false)

    } catch (error) {
      console.error('Error submitting vendor form:', error)

      // Display appropriate error message
      if (error.response) {
        const errorMessage = error.response.data?.message || error.response.data?.error || error.response.statusText
        message.error(`Failed to ${editingVendor ? 'update' : 'add'} vendor: ${errorMessage}`)
      } else if (error.request) {
        message.error('Content is too large. Please reduce the file size and try again.')
      } else {
        message.error(`Failed to ${editingVendor ? 'update' : 'add'} vendor: ` + (error.message || 'Unknown error'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setBirFileList([])
    setGeneralFileList([])
    setIsFormValid(false)
    onCancel()
  }

  const birUploadProps = {
    name: 'birAttachment',
    multiple: false,
    fileList: birFileList,
    beforeUpload: () => {
      // Prevent auto upload
      return false
    },
    onChange: ({ fileList: newFileList }) => {
      // Validate file type and size for new files
      const validatedFileList = newFileList.filter(fileItem => {
        if (fileItem.isExisting) return true // Skip validation for existing files

        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png']
        if (!allowedTypes.includes(fileItem.type)) {
          message.error('Only PDF, DOC, DOCX, JPG, and PNG files are allowed for BIR attachment')
          return false
        }

       

        return true
      })

      // Only keep the latest file (maxCount: 1)
      setBirFileList(validatedFileList.slice(-1))
    },
    onRemove: () => {
      // Allow removal of both new and existing files
      setBirFileList([])
    },
    maxCount: 1,
    accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png'
  }

  const generalUploadProps = {
    name: 'generalAttachments',
    multiple: true,
    fileList: generalFileList,
    beforeUpload: () => {
      // Prevent auto upload
      return false
    },
    onChange: ({ fileList: newFileList }) => {
      // Validate file type and size for new files
      const validatedFileList = newFileList.filter(fileItem => {
        if (fileItem.isExisting) return true // Skip validation for existing files

        const allowedTypes = [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'image/jpeg',
          'image/jpg',
          'image/png',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ]
        if (!allowedTypes.includes(fileItem.type)) {
          message.error('Only PDF, DOC, DOCX, JPG, PNG, XLS, and XLSX files are allowed')
          return false
        }

      

        return true
      })

      setGeneralFileList(validatedFileList)
    },
    onRemove: (fileToRemove) => {
      const index = generalFileList.indexOf(fileToRemove)
      const newFileList = generalFileList.slice()
      newFileList.splice(index, 1)
      setGeneralFileList(newFileList)
      return true // Allow removal
    },
    accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx'
  }

  return (
    <Modal
      title={
        <span style={{ fontSize: '20px', fontWeight: '600' }}>
          {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
        </span>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFieldsChange={handleFormChange}
        requiredMark={!editingVendor} // Only show required marks for new vendors
      >
        {/* Vendor Name */}
        <Form.Item
          name="vendorName"
          label="Vendor Name"
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            { required: !editingVendor, message: 'Please enter vendor name' },
            { min: 2, message: 'Vendor name must be at least 2 characters' },
            { max: 250, message: 'Vendor name cannot exceed 250 characters' },
            // Allow letters, numbers, spaces and common punctuation (- . , & ( ) ')
            { pattern: /^[A-Za-z0-9\s\-\.,&()']+$/, message: 'Vendor name contains invalid characters' }
          ]}
        >
          <Input
            placeholder="Enter vendor name"
            maxLength={250}
          />
        </Form.Item>

        {/* TIN No - Made optional for editing */}
        <Form.Item
          name="tinNo"
          label="TIN Number"
          validateTrigger={['onChange', 'onBlur']}
          rules={editingVendor ? [
            // For editing: only validate pattern if value is provided
            { pattern: /^[0-9]*$/, message: 'TIN number must contain only numbers' }
          ] : [
            // For new vendors: all validations apply
            { required: true, message: 'Please enter TIN number' },
            { pattern: /^[0-9]+$/, message: 'TIN number must contain only numbers' },
            { max: 30, message: 'TIN number cannot exceed 30 characters' }
          ]}
        >
          <Input
            placeholder={editingVendor ? 'Enter TIN number (optional)' : 'Enter TIN number'}
            maxLength={editingVendor ? undefined : 30}
          />
        </Form.Item>

        {/* SAP Reference ID - Optional */}
        <Form.Item
          name="sapReferenceId"
          label="SAP Reference ID (Optional)"
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            { max: 50, message: 'SAP Reference ID cannot exceed 50 characters' },
            // Allow letters, numbers, hyphen and slash only
            { pattern: /^[A-Za-z0-9\-\/]+$/, message: 'SAP Reference ID contains invalid characters' }
          ]}
        >
          <Input
            placeholder="Enter SAP Reference ID"
            maxLength={50}
          />
        </Form.Item>

        {/* Vendor Address */}
        <Form.Item
          name="vendorAddress"
          label="Vendor Address"
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            { required: !editingVendor, message: 'Please enter vendor address' },
            { min: 10, message: 'Address must be at least 10 characters' },
            // Allow letters, numbers, spaces and common address punctuation (# , . - / \ ( ) ')
            { pattern: /^[A-Za-z0-9\s\-\.,#\/\\()']+$/, message: 'Address contains invalid characters' }
          ]}
        >
          <TextArea
            placeholder="Enter complete vendor address"
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>

        {/* BIR Attachment - Only show for new vendors */}
        {!editingVendor && (
          <Form.Item
            name="birAttachment"
            label="BIR Attachment (Required)"
            rules={[
              {
                required: true,
                validator: () => {
                  if (birFileList.length === 0) {
                    return Promise.reject(new Error('Please upload BIR attachment'))
                  }
                  return Promise.resolve()
                }
              }
            ]}
          >
            <Dragger {...birUploadProps} style={{ padding: '16px' }}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
              </p>
              <p className="ant-upload-text" style={{ fontSize: '14px', marginBottom: '4px' }}>
                Click or drag BIR document to this area to upload
              </p>
              <p className="ant-upload-hint" style={{ color: '#666', fontSize: '12px' }}>
                Required: Upload BIR registration document (PDF, DOC, DOCX, JPG, PNG)
              </p>
            </Dragger>
          </Form.Item>
        )}

        {/* General Attachments - Only show for new vendors */}
        {!editingVendor && (
          <Form.Item
            name="generalAttachments"
            label="Additional Attachments (Optional)"
          >
            <Dragger {...generalUploadProps} style={{ padding: '16px' }}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ fontSize: '32px', color: '#52c41a' }} />
              </p>
              <p className="ant-upload-text" style={{ fontSize: '14px', marginBottom: '4px' }}>
                Click or drag additional files to this area to upload
              </p>
              <p className="ant-upload-hint" style={{ color: '#666', fontSize: '12px' }}>
                Optional: Upload additional documents (PDF, DOC, DOCX, JPG, PNG, XLS, XLSX)
              </p>
            </Dragger>
          </Form.Item>
        )}

        {/* Form Actions */}
        <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!isFormValid || loading}
            >
              {editingVendor ? 'Update Vendor' : 'Add Vendor'}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default VendorForm